"use strict";
// (C) 2022 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAttributeFilterButtonState = void 0;
var tslib_1 = require("tslib");
var types_1 = require("../../AttributeDropdown/types");
var react_1 = require("react");
var constants_1 = require("../constants");
var AttributeFilterButtonUtils_1 = require("../AttributeFilterButtonUtils");
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var mergeElementQueryResults_1 = require("../../AttributeDropdown/mergeElementQueryResults");
var AttributeFilterUtils_1 = require("../../utils/AttributeFilterUtils");
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
/**
 * Provides a {@link IAttributeFilterButtonState} and a set of functions to handle the state
 * of {@link AttributeFilterButton} component.
 *
 * @param currentFilter - the filter object to initialize the component's state.
 */
exports.useAttributeFilterButtonState = function (currentFilter, backend) {
    /**
     * AttributeFilterButton state initialization
     */
    var _a = react_1.useState(function () {
        AttributeFilterButtonUtils_1.checkFilterSetupForBackend(currentFilter, backend);
        var initialSelection = AttributeFilterButtonUtils_1.getInitialSelectedOptions(currentFilter);
        var initialIsInverted = AttributeFilterButtonUtils_1.getInitialIsInverted(currentFilter);
        return {
            selectedFilterOptions: initialSelection,
            appliedFilterOptions: initialSelection,
            isInverted: initialIsInverted,
            appliedIsInverted: initialIsInverted,
            firstLoad: true,
            searchString: "",
            offset: 0,
            limit: constants_1.ATTRIBUTE_FILTER_BUTTON_LIMIT,
            isDropdownOpen: false,
            validOptions: null,
            uriToAttributeElementMap: new Map(),
            isFiltering: false,
            needsReloadAfterSearch: false,
        };
    }), state = _a[0], setState = _a[1];
    var onCurrentFilterChange = function (currentFilter) {
        setState(function (prevValue) {
            var initialSelection = AttributeFilterButtonUtils_1.getInitialSelectedOptions(currentFilter);
            var initialIsInverted = AttributeFilterButtonUtils_1.getInitialIsInverted(currentFilter);
            // todo check if there is need to handle values here
            var selectedOptionsUris = prevValue.selectedFilterOptions.map(function (opt) { return opt.uri; });
            var appliedOptionsUris = prevValue.appliedFilterOptions.map(function (opt) { return opt.uri; });
            var initialSelectionUris = initialSelection.map(function (opt) { return opt.uri; });
            var resultState = prevValue;
            if (!isEqual_1.default(selectedOptionsUris, initialSelectionUris)) {
                resultState = tslib_1.__assign(tslib_1.__assign({}, resultState), { selectedFilterOptions: initialSelection });
            }
            if (!isEqual_1.default(appliedOptionsUris, initialSelectionUris)) {
                resultState = tslib_1.__assign(tslib_1.__assign({}, resultState), { appliedFilterOptions: initialSelection });
            }
            if (prevValue.isInverted !== initialIsInverted) {
                resultState = tslib_1.__assign(tslib_1.__assign({}, resultState), { isInverted: initialIsInverted, appliedIsInverted: initialIsInverted });
            }
            // if no change returning prevValue effectively skips the setState
            return resultState;
        });
    };
    var clearUriToElementMap = function () {
        setState(function (prevState) {
            return tslib_1.__assign(tslib_1.__assign({}, prevState), { uriToAttributeElementMap: new Map() });
        });
    };
    var resetSelection = function () {
        setState(function (prevState) {
            return tslib_1.__assign(tslib_1.__assign({}, prevState), { selectedFilterOptions: [], appliedFilterOptions: [], isInverted: true, appliedIsInverted: true, validOptions: null, isFiltering: true });
        });
    };
    var mapInitialSelectionElements = function (initialElements, isElementsByRef) {
        setState(function (prevState) {
            var _a;
            var uriToAttributeElementMap = new Map(prevState.uriToAttributeElementMap);
            (_a = initialElements.items) === null || _a === void 0 ? void 0 : _a.forEach(function (item) {
                var key = isElementsByRef ? item.uri : item.title;
                uriToAttributeElementMap.set(key, item);
            });
            return tslib_1.__assign(tslib_1.__assign({}, prevState), { uriToAttributeElementMap: uriToAttributeElementMap });
        });
    };
    var resolveAttributeElements = function (elements, parentFilters, isElementsByRef) {
        setState(function (prevState) {
            var mergedValidElements = mergeElementQueryResults_1.mergeElementQueryResults(prevState.validOptions, elements);
            var newUriToAttributeElementMap = new Map(prevState.uriToAttributeElementMap);
            var items = mergedValidElements.items;
            items.filter(types_1.isNonEmptyListItem).forEach(function (item) {
                var key = isElementsByRef ? item.uri : item.title;
                newUriToAttributeElementMap.set(key, item);
            });
            // make sure that selected items have both title and uri, otherwise selection in InvertableList won't work
            // TODO we could maybe use the InvertableList's getItemKey and just use title or uri for example
            var updatedSelectedItems = AttributeFilterUtils_1.updateSelectedOptionsWithDataByMap(prevState.selectedFilterOptions, newUriToAttributeElementMap, isElementsByRef);
            var updatedAppliedItems = AttributeFilterUtils_1.updateSelectedOptionsWithDataByMap(prevState.appliedFilterOptions, newUriToAttributeElementMap, isElementsByRef);
            var validOptions = (parentFilters === null || parentFilters === void 0 ? void 0 : parentFilters.length) ? elements : mergedValidElements;
            return tslib_1.__assign(tslib_1.__assign({}, prevState), { selectedFilterOptions: compact_1.default(updatedSelectedItems), appliedFilterOptions: compact_1.default(updatedAppliedItems), validOptions: validOptions, firstLoad: false, uriToAttributeElementMap: newUriToAttributeElementMap, needsReloadAfterSearch: false });
        });
    };
    var onSearch = react_1.useCallback(debounce_1.default(function (query) {
        setState(function (s) { return (tslib_1.__assign(tslib_1.__assign({}, s), { searchString: query })); });
    }, 500), []);
    var onElementSelect = function (selectedFilterOptions, isInverted) {
        setState(function (s) { return (tslib_1.__assign(tslib_1.__assign({}, s), { selectedFilterOptions: selectedFilterOptions, isInverted: isInverted })); });
    };
    var backupIsInverted = function () {
        setState(function (state) { return (tslib_1.__assign(tslib_1.__assign({}, state), { appliedIsInverted: state.isInverted })); });
    };
    var removeFilteringStatus = function () {
        setState(function (prevState) {
            return tslib_1.__assign(tslib_1.__assign({}, prevState), { isFiltering: false });
        });
    };
    var onRangeChange = function (_searchString, from, to) {
        // only react to range changes after initial load to properly handle offset shifts on search
        if (state.validOptions) {
            setState(function (s) { return (tslib_1.__assign(tslib_1.__assign({}, s), { offset: from, limit: to - from })); });
        }
    };
    var onDropdownClosed = function () {
        setState(function (s) {
            return tslib_1.__assign(tslib_1.__assign({}, s), { selectedFilterOptions: s.appliedFilterOptions, isInverted: s.appliedIsInverted, searchString: "", isDropdownOpen: false });
        });
    };
    var onDropdownOpen = function () {
        setState(function (s) { return (tslib_1.__assign(tslib_1.__assign({}, s), { isDropdownOpen: true })); });
    };
    /**
     * Effects
     */
    react_1.useEffect(function () {
        setState(function (prevState) {
            return tslib_1.__assign(tslib_1.__assign({}, prevState), { validOptions: null, offset: 0, limit: constants_1.ATTRIBUTE_FILTER_BUTTON_LIMIT, needsReloadAfterSearch: true });
        });
    }, [state.searchString]);
    return {
        state: state,
        onCurrentFilterChange: onCurrentFilterChange,
        clearUriToElementMap: clearUriToElementMap,
        resetSelection: resetSelection,
        mapInitialSelectionElements: mapInitialSelectionElements,
        resolveAttributeElements: resolveAttributeElements,
        onSearch: onSearch,
        onElementSelect: onElementSelect,
        backupIsInverted: backupIsInverted,
        removeFilteringStatus: removeFilteringStatus,
        onRangeChange: onRangeChange,
        onDropdownClosed: onDropdownClosed,
        onDropdownOpen: onDropdownOpen,
    };
};
//# sourceMappingURL=useAttributeFilterButtonState.js.map